import { DenormalizedCustomizableQuestion } from '@packages/types'
import React from 'react'

import * as customizationUtils from 'customizer/customization/utils'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import CheckboxAnswer from 'themes/common/components/answers/CheckboxAnswer'
import DescriptionTooltip from 'themes/common/components/answers/DescriptionTooltip'

import './CheckboxQuestion.scss'

export interface CheckboxQuestionProps {
  className?: string
  question: DenormalizedCustomizableQuestion
  pricing?: Record<string, string>
  currency: string
  lang?: string
  country?: string
  focusQuestion: () => void
  selectAnswer: (answerId: string) => void
  answersOutOfStock: string[]
}

const CheckboxQuestion = ({
  className,
  question,
  pricing,
  currency,
  lang,
  country,
  focusQuestion,
  selectAnswer,
  answersOutOfStock,
}: CheckboxQuestionProps) => {
  const answerStyle = useThemeSettings('answer')
  const priceStyle = useThemeSettings('price')

  const handleAnswerClick = (answerId: string) => {
    selectAnswer(answerId)
    focusQuestion()
  }

  const selectionsLeft = customizationUtils.countSelectionsLeft(question)

  return (
    <div
      className={`checkbox-question ${className}-option ${className}-option-${question.type}`}
      data-testid="checkbox-question"
    >
      <div className="choices checkbox-question__choices">
        {question.answers.map(answer => {
          const isOutOfStock = answersOutOfStock.includes(answer.id)

          return (
            <DescriptionTooltip
              allowHeaderOnly={false}
              key={`tooltip-${answer.id}`}
              description={answer.description}
              showDescription={answer.showDescription}
              header={answer.name}
              style={answerStyle}
            >
              <CheckboxAnswer
                key={answer.id}
                {...answer}
                price={pricing?.[answer.id]}
                currency={currency}
                lang={lang}
                country={country}
                selected={!!question.selectedAnswers!.find(selectedAnswer => selectedAnswer.id === answer.id)}
                handleClick={answerId => handleAnswerClick(answerId)}
                disabled={
                  selectionsLeft === 0 &&
                  question.selectedAnswers!.find(selectedAnswer => selectedAnswer.id === answer.id) == null
                }
                style={{
                  text: {
                    color: answerStyle.answerFontColor,
                    fontSize: answerStyle.answerFontSize,
                    fontFamily: answerStyle.answerFontFamily?.fontFamily,
                  },
                  price: {
                    fontFamily: priceStyle.priceFont?.fontFamily,
                    color: priceStyle.extraPriceFontColor,
                    borderColor: priceStyle.extraPriceBorderColor,
                    backgroundColor: priceStyle.extraPriceBackgroundColor,
                  },
                  selectedColor: answerStyle.selectedAnswerBorderColor,
                  unselectedColor: answerStyle.unselectedAnswerBorderColor,
                }}
                isOutOfStock={isOutOfStock}
              />
            </DescriptionTooltip>
          )
        })}
      </div>
    </div>
  )
}

export default CheckboxQuestion
